import {
    AdminApi, CandidateGrid, ScheduledGrid, Slot, TrackSlots, SyncReport, VerifyReport,
    RemoteChallengeScores,
    lifecycleOf, clearAdminToken,
} from '../api/AdminApi.js';
import { AuthoringState } from '../persistence/types.js';
import { RESERVED_USERNAMES } from '../shared/scoring.js';

export interface AdminPanelCallbacks {
    onClose: () => void;
    /** Play a candidate grid: add it to the local portfolio if absent, then switch to it. */
    onPlay: (gridData: string) => void;
    /**
     * Serialize the curator's local play of a grid (won games only) into draft
     * solution_data, or null if it was never played locally / nothing is won.
     */
    onCaptureGyrodiot: (gridData: string) => { data: string; solved: number } | null;
    /** Games won on the local copy of a grid, or null if never played locally. */
    onLocalPlayState: (gridData: string) => number | null;
    /**
     * The pipeline's candidate + scheduled sets, on every successful fetch — lets the
     * portfolio label each grid with the stage the backlog actually has it at.
     */
    onAuthoringStateLoaded: (state: AuthoringState) => void;
}

/** Per-section options for an actionable grid row. */
interface GridRowOptions {
    /** Optional chip label (e.g. 'default'). Defaults to the row's lifecycle stage. */
    tag?: string;
    /** Whether to show the Schedule button (default true). */
    allowSchedule?: boolean;
    /** Whether to show the Delete button (backlog only; default false). */
    allowDelete?: boolean;
}

const shortDate = (iso: string) => iso.slice(0, 10);

/**
 * In-app challenge-authoring panel (build-gated behind VITE_ADMIN). Renders into
 * an overlay element and talks to /api/admin/* on the local server. Three actions:
 * Generate candidates, Solve as Quadrant (async job), Schedule into next weekly slot.
 */
export class AdminPanel {
    private containerEl: HTMLElement;
    private callbacks: AdminPanelCallbacks;
    private grids: CandidateGrid[] = [];
    private defaults: CandidateGrid[] = [];
    private scheduled: ScheduledGrid[] = [];
    private slots: TrackSlots = { base: [], vortex: [] };
    private allScope = '';
    private statusMsg = '';
    private jobPollTimer: number | null = null;
    private activeJob: { gridId: number; lines: string[]; status: string } | null = null;
    private syncLines: string[] | null = null;
    /** Remote leaderboards currently expanded inline, keyed by local challenge id. */
    private boards: Map<number, RemoteChallengeScores> = new Map();
    /** Slot chosen in each scheduled row's picker, keyed by challenge id. */
    private slotChoice: Map<number, string> = new Map();

    constructor(containerEl: HTMLElement, callbacks: AdminPanelCallbacks) {
        this.containerEl = containerEl;
        this.callbacks = callbacks;
    }

    show(): void {
        this.containerEl.classList.remove('hidden');
        void this.refresh();
    }

    hide(): void {
        if (this.jobPollTimer !== null) { clearInterval(this.jobPollTimer); this.jobPollTimer = null; }
        this.activeJob = null;
        this.containerEl.classList.add('hidden');
    }

    private async refresh(): Promise<void> {
        try {
            const data = await AdminApi.candidates();
            this.grids = data.grids;
            this.defaults = data.defaults;
            this.scheduled = data.scheduled;
            this.slots = data.slots;
            this.allScope = data.all_scope;
            // Authoritative pipeline state — only safe to reconcile against after a
            // successful fetch, never on the error path (that would clear every label).
            this.callbacks.onAuthoringStateLoaded({
                candidates: data.grids.map(g => g.grid_data),
                scheduled: data.scheduled.map(g => ({
                    gridData: g.grid_data, title: g.challenge.title, startsAt: g.challenge.starts_at,
                })),
            });
        } catch (err: any) {
            this.statusMsg = `Error: ${err.message}`;
        }
        this.render();
    }

    private setStatus(msg: string): void {
        this.statusMsg = msg;
        this.render();
    }

    // --- Actions ----------------------------------------------------------

    private async doGenerate(count: number, sizes: number[]): Promise<void> {
        this.setStatus('Generating…');
        try {
            const r = await AdminApi.generate(count, sizes.length ? sizes : undefined);
            this.setStatus(`Generated ${r.grids.length} grid(s).`);
            await this.refresh();
        } catch (err: any) {
            this.setStatus(`Generate failed: ${err.message}`);
        }
    }

    private async doCapture(g: CandidateGrid): Promise<void> {
        const captured = this.callbacks.onCaptureGyrodiot(g.grid_data);
        if (!captured) {
            this.setStatus('Nothing to capture — play this grid (via Play) and win at least one game first.');
            return;
        }
        this.setStatus(`Capturing Gyrodiot draft (${captured.solved}/12)…`);
        try {
            const r = await AdminApi.saveDraft(g.id, captured.data);
            this.setStatus(`Captured Gyrodiot draft: ${r.solved}/12.${r.attached ? ` Attached to challenge — ${r.attached}` : ''}`);
            await this.refresh();
        } catch (err: any) {
            this.setStatus(`Capture failed: ${err.message}`);
        }
    }

    private async doSolve(gridId: number): Promise<void> {
        try {
            const { job_id } = await AdminApi.solve(gridId);
            this.activeJob = { gridId, lines: [], status: 'running' };
            this.setStatus(`Solving grid ${gridId} (Quadrant)…`);
            if (this.jobPollTimer !== null) clearInterval(this.jobPollTimer);
            this.jobPollTimer = window.setInterval(() => this.pollJob(job_id), 1500);
        } catch (err: any) {
            this.setStatus(`Solve failed to start: ${err.message}`);
        }
    }

    private async pollJob(jobId: string): Promise<void> {
        try {
            const job = await AdminApi.job(jobId);
            if (this.activeJob) { this.activeJob.lines = job.lines; this.activeJob.status = job.status; }
            if (job.status !== 'running') {
                if (this.jobPollTimer !== null) { clearInterval(this.jobPollTimer); this.jobPollTimer = null; }
                this.statusMsg = job.status === 'done' ? 'Solve complete.' : `Solve ${job.status}.`;
                await this.refresh(); // draft status now updated
                return;
            }
            this.render();
        } catch {
            // transient; keep polling
        }
    }

    private async doSync(): Promise<void> {
        this.setStatus('Syncing to remote…');
        this.boards.clear();
        try {
            const r = await AdminApi.sync();
            this.syncLines = this.formatSync(r);
            this.setStatus(r.ok ? 'Sync complete.' : `Sync finished with errors${r.error ? ': ' + r.error : ''}.`);
            // Sync stamps synced_at (freezing challenges and clearing the "unpushed"
            // par drift), so re-fetch to reflect the new published state. Runs even on
            // partial failure — markChallengeSynced fires per challenge inside the push.
            await this.refresh();
        } catch (err: any) {
            this.syncLines = null;
            this.setStatus(`Sync failed: ${err.message}`);
        }
    }

    private formatSync(r: SyncReport): string[] {
        const lines = [`remote: ${r.remote ?? '(unset)'}`];
        if (r.error) lines.push(`error: ${r.error}`);
        for (const c of r.challenges) {
            if (c.error) { lines.push(`✗ ${c.title}: ${c.error}`); continue; }
            lines.push(`✓ ${c.title} → #${c.remoteId}`);
            for (const s of c.solutions ?? []) lines.push(`    ${s}`);
        }
        return lines;
    }

    /** Reconcile the local synced_at stamps against what the remote actually serves. */
    private async doVerify(): Promise<void> {
        this.setStatus('Verifying against remote…');
        this.boards.clear();
        try {
            const r = await AdminApi.verify();
            this.syncLines = this.formatVerify(r);
            this.setStatus(r.ok
                ? (r.corrected ? `Verify complete — corrected ${r.corrected} stamp(s).` : 'Verify complete — all stamps correct.')
                : `Verify failed${r.error ? ': ' + r.error : ''}.`);
            if (r.ok) await this.refresh();
        } catch (err: any) {
            this.syncLines = null;
            this.setStatus(`Verify failed: ${err.message}`);
        }
    }

    private formatVerify(r: VerifyReport): string[] {
        const lines = [`verify against: ${r.remote ?? '(unset)'}`];
        if (r.error) lines.push(`error: ${r.error}`);
        lines.push(...r.lines);
        return lines;
    }

    private static isRef = (name: string | null) => (RESERVED_USERNAMES as readonly string[]).includes(name ?? '');

    /**
     * Pull every published challenge's player leaderboard from the remote and expand them
     * all inline. Player solutions live only on the remote, so this is the sole way to see
     * who has played from the authoring instance.
     */
    private async doPullAllScores(): Promise<void> {
        this.setStatus('Fetching remote leaderboards…');
        this.syncLines = null;
        try {
            const r = await AdminApi.remoteScores();
            this.boards = new Map(r.challenges.map(c => [c.local_id, c]));
            const players = r.challenges.reduce(
                (n, c) => n + c.entries.filter(e => !AdminPanel.isRef(e.username)).length, 0);
            this.setStatus(r.ok
                ? `Fetched ${r.challenges.length} board(s) — ${players} player entr${players === 1 ? 'y' : 'ies'}.`
                : `Fetched with errors${r.error ? ': ' + r.error : ''}.`);
        } catch (err: any) {
            this.setStatus(`Pull scores failed: ${err.message}`);
        }
    }

    /** Toggle one challenge's remote leaderboard inline: fetch + expand, or collapse if already open. */
    private async toggleBoard(localChallengeId: number): Promise<void> {
        if (this.boards.has(localChallengeId)) {
            this.boards.delete(localChallengeId);
            this.render();
            return;
        }
        this.setStatus('Fetching leaderboard…');
        this.syncLines = null;
        try {
            const r = await AdminApi.remoteScores(localChallengeId);
            const board = r.challenges.find(c => c.local_id === localChallengeId);
            if (board) {
                this.boards.set(localChallengeId, board);
                this.setStatus('');
            } else {
                this.setStatus(r.error ? `Error: ${r.error}` : 'Not published on the remote yet.');
            }
        } catch (err: any) {
            this.setStatus(`Pull scores failed: ${err.message}`);
        }
    }

    private async doReschedule(g: ScheduledGrid): Promise<void> {
        const startsAt = this.slotChoice.get(g.challenge.id) ?? this.slotsFor(g)[0]?.starts_at;
        if (!startsAt) { this.setStatus('No free slot available.'); return; }
        if (startsAt === g.challenge.starts_at) { this.setStatus('Already in that slot.'); return; }
        this.setStatus(`Rescheduling "${g.challenge.title}"…`);
        try {
            const r = await AdminApi.reschedule(g.challenge.id, startsAt);
            this.slotChoice.delete(g.challenge.id);
            this.setStatus(`Rescheduled to "${r.title}" (${shortDate(r.starts_at)}).`);
            await this.refresh();
        } catch (err: any) {
            this.setStatus(`Reschedule failed: ${err.message}`);
        }
    }

    private async doUnschedule(g: ScheduledGrid): Promise<void> {
        if (!window.confirm(
            `Unschedule "${g.challenge.title}"? The grid returns to the backlog and its attached `
            + `reference solutions are dropped. Drafts are kept, so re-scheduling restores them.`
        )) return;
        this.setStatus(`Unscheduling "${g.challenge.title}"…`);
        try {
            await AdminApi.unschedule(g.challenge.id);
            this.setStatus(`Unscheduled — grid ${g.grid_hash} is back in the backlog.`);
            await this.refresh();
        } catch (err: any) {
            this.setStatus(`Unschedule failed: ${err.message}`);
        }
    }

    /** "+T −D" for a set that differs from the default, or '' when it matches (or is unset). */
    private scopeDiff(scope: string | null): string {
        if (!scope || !this.allScope || scope === this.allScope) return '';
        const inScope = new Set(scope.split(''));
        const inDefault = new Set(this.allScope.split(''));
        const added = [...inScope].filter(i => !inDefault.has(i)).sort();
        const removed = [...inDefault].filter(i => !inScope.has(i)).sort();
        if (!added.length && !removed.length) return '';
        return `set ${added.map(i => `+${i}`).join(' ')}${added.length && removed.length ? ' ' : ''}${removed.map(i => `−${i}`).join(' ')}`.trim();
    }

    private async doSchedule(gridId: number, scope: string): Promise<void> {
        this.setStatus(`Scheduling grid ${gridId}…`);
        try {
            const r = await AdminApi.schedule(gridId, scope);
            this.setStatus(`Scheduled "${r.title}" (${r.starts_at.slice(0, 10)}). Attached: ${r.attached.join('; ') || 'none'}`);
            await this.refresh();
        } catch (err: any) {
            this.setStatus(`Schedule failed: ${err.message}`);
        }
    }

    private async doDelete(g: CandidateGrid): Promise<void> {
        if (!window.confirm(`Delete backlog grid ${g.grid_hash}? This removes its reference drafts too.`)) return;
        this.setStatus(`Deleting grid ${g.id}…`);
        try {
            await AdminApi.deleteGrid(g.id);
            this.setStatus(`Deleted grid ${g.grid_hash}.`);
            await this.refresh();
        } catch (err: any) {
            this.setStatus(`Delete failed: ${err.message}`);
        }
    }

    // --- Render -----------------------------------------------------------

    private render(): void {
        const el = this.containerEl;
        el.innerHTML = '';

        // Header
        const header = document.createElement('div');
        header.className = 'admin-header';
        const title = document.createElement('span');
        title.className = 'admin-title';
        title.textContent = 'Admin · Challenge Authoring';
        const spacer = document.createElement('span');
        spacer.style.flex = '1';
        const syncBtn = this.button('Sync to remote', () => this.doSync());
        const scoresBtn = this.button('Pull remote scores', () => this.doPullAllScores());
        scoresBtn.classList.add('admin-btn-secondary');
        scoresBtn.title = 'Fetch player leaderboards from the remote for every published challenge';
        const verifyBtn = this.button('Verify', () => this.doVerify());
        verifyBtn.classList.add('admin-btn-secondary');
        verifyBtn.title = 'Check which challenges are actually live on the remote, and correct the local published/editable state';
        const resetTokenBtn = this.button('Reset token', () => { clearAdminToken(); this.setStatus('Admin token cleared.'); });
        resetTokenBtn.classList.add('admin-btn-secondary');
        const closeBtn = this.button('Close', () => this.callbacks.onClose());
        header.append(title, spacer, syncBtn, scoresBtn, verifyBtn, resetTokenBtn, closeBtn);
        el.appendChild(header);

        // Generate row
        const gen = document.createElement('div');
        gen.className = 'admin-section';
        const countInput = document.createElement('input');
        countInput.type = 'number'; countInput.min = '1'; countInput.max = '50'; countInput.value = '5';
        countInput.className = 'admin-input';
        const sizeBoxes: HTMLInputElement[] = [];
        const sizeWrap = document.createElement('span');
        sizeWrap.className = 'admin-sizes';
        for (const s of [6, 8, 10]) {
            const lbl = document.createElement('label');
            const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = String(s); cb.checked = true;
            sizeBoxes.push(cb);
            lbl.append(cb, document.createTextNode(` ${s}×${s}`));
            sizeWrap.appendChild(lbl);
        }
        const genBtn = this.button('Generate', () => {
            const sizes = sizeBoxes.filter(b => b.checked).map(b => Number(b.value));
            this.doGenerate(Number(countInput.value) || 5, sizes);
        });
        gen.append(this.label('Generate'), countInput, this.label('sizes:'), sizeWrap, genBtn);
        el.appendChild(gen);

        // Status line
        if (this.statusMsg) {
            const st = document.createElement('div');
            st.className = 'admin-status';
            st.textContent = this.statusMsg;
            el.appendChild(st);
        }

        // Active job log
        if (this.activeJob) {
            const log = document.createElement('pre');
            log.className = 'admin-joblog';
            log.textContent = this.activeJob.lines.join('\n') || '(starting…)';
            el.appendChild(log);
        }

        // Sync report
        if (this.syncLines) {
            const log = document.createElement('pre');
            log.className = 'admin-joblog';
            log.textContent = this.syncLines.join('\n');
            el.appendChild(log);
        }

        // Backlog list
        const listTitle = document.createElement('div');
        listTitle.className = 'admin-subtitle';
        listTitle.textContent = `Backlog (${this.grids.length})`;
        el.appendChild(listTitle);

        const list = document.createElement('div');
        list.className = 'admin-list';
        if (this.grids.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'admin-empty';
            empty.textContent = 'No candidate grids. Generate some above.';
            list.appendChild(empty);
        }
        for (const g of this.grids) {
            list.appendChild(this.gridRow(g, { allowDelete: true }));
        }
        el.appendChild(list);

        // Default grids: actionable like the backlog (play / solve / capture), but
        // not schedulable — they ship in every portfolio, so they can't back a challenge.
        this.renderGridSection(el, `Default grids (${this.defaults.length})`, this.defaults, { tag: 'default', allowSchedule: false });

        // Scheduled challenges: same actions as any other grid, plus reschedule /
        // unschedule while un-pushed. Each row's chip says which it is.
        const published = this.scheduled.filter(g => lifecycleOf(g) === 'published').length;
        this.renderGridSection(
            el,
            `Scheduled challenges (${this.scheduled.length}${published ? ` · ${published} published` : ''})`,
            this.scheduled,
            { allowSchedule: false },
        );
    }

    /**
     * One challenge's remote leaderboard, rendered inline under its row. Real players are
     * highlighted over the Quadrant/Gyrodiot reference rows.
     */
    private remoteBoard(c: RemoteChallengeScores): HTMLElement {
        const box = document.createElement('div');
        box.className = 'admin-board';

        if (c.error) {
            const e = document.createElement('div');
            e.className = 'admin-draft-pending';
            e.textContent = `error: ${c.error}`;
            box.appendChild(e);
            return box;
        }
        if (c.entries.length === 0) {
            const e = document.createElement('div');
            e.className = 'admin-empty';
            e.textContent = 'No submissions yet.';
            box.appendChild(e);
            return box;
        }

        const table = document.createElement('table');
        table.className = 'admin-board-table';
        for (const e of c.entries) {
            const tr = document.createElement('tr');
            if (!AdminPanel.isRef(e.username)) tr.className = 'admin-board-player';
            const rank = document.createElement('td'); rank.textContent = `#${e.rank}`;
            const name = document.createElement('td'); name.textContent = e.username || '(anon)';
            const score = document.createElement('td');
            score.textContent = `${e.total_score}${e.unsolved_count ? ` · ${e.unsolved_count} unsolved` : ''}`;
            const bd = document.createElement('td');
            bd.className = 'admin-board-breakdown';
            bd.textContent = e.score_breakdown;
            tr.append(rank, name, score, bd);
            table.appendChild(tr);
        }
        box.appendChild(table);
        return box;
    }

    /** Render a titled list of actionable grid rows (same controls as the backlog). */
    private renderGridSection(el: HTMLElement, title: string, grids: (CandidateGrid | ScheduledGrid)[], opts: GridRowOptions): void {
        const subtitle = document.createElement('div');
        subtitle.className = 'admin-subtitle';
        subtitle.textContent = title;
        el.appendChild(subtitle);

        const list = document.createElement('div');
        list.className = 'admin-list';
        for (const g of grids) {
            list.appendChild(this.gridRow(g, opts));
            // A published challenge whose leaderboard is expanded shows it right below
            // its own row, so each board sits with the challenge it belongs to.
            const sched = 'challenge' in g ? g : null;
            const board = sched && this.boards.get(sched.challenge.id);
            if (board) list.appendChild(this.remoteBoard(board));
        }
        el.appendChild(list);
    }

    private gridRow(g: CandidateGrid | ScheduledGrid, opts: GridRowOptions = {}): HTMLElement {
        const sched = 'challenge' in g ? g : null;
        const stage = lifecycleOf(g);
        const frozen = stage === 'published';

        const row = document.createElement('div');
        row.className = 'admin-grid-row';
        if (frozen) row.classList.add('admin-grid-row-frozen');
        // Vortex wears purple everywhere else in the app (hub, gates, tutorials), so a
        // Vortex row's accents follow suit rather than reading as another Base row.
        if (g.is_vortex) row.classList.add('admin-grid-row-vortex');

        const info = document.createElement('div');
        info.className = 'admin-grid-info';

        // Line 1 — what this row *is*: stage, challenge number, grid, when.
        const identity = document.createElement('div');
        identity.className = 'admin-grid-line';
        // Every row carries its lifecycle stage, so 'why can't I touch this?' is
        // answerable at a glance rather than by remembering what you synced.
        const tag = opts.tag ?? stage;
        const chip = document.createElement('span');
        chip.className = `admin-tag admin-tag-${tag}`;
        chip.textContent = tag;
        identity.append(chip, document.createTextNode(' '));
        // The local challenge id, right after the badge — it is the handle every
        // challenge-level action takes (reschedule, unschedule, pull scores, CLI),
        // and it is not the remote's `#id`, which the published chip shows instead.
        if (sched) {
            const num = document.createElement('span');
            num.className = 'admin-challenge-id';
            num.textContent = `#${sched.challenge.id}`;
            num.title = 'Local challenge id';
            identity.append(num, document.createTextNode(' '));
        }
        const hash = document.createElement('span');
        hash.className = 'admin-grid-hash';
        hash.textContent = g.grid_hash;
        const meta = document.createElement('span');
        meta.className = 'admin-grid-meta';
        identity.append(hash, meta);

        // Meta parts are joined rather than each appending its own separator, so the
        // line never ends on a dangling ' · ' now that the scores have moved off it.
        const metaParts: Array<Node | string> = [`${g.size}×${g.size}${g.is_vortex ? ' vortex' : ''}`];

        // A non-classic game set is the whole point of the 13-game pool, and it is invisible
        // in the grid data — so surface it as a diff against the default (e.g. "set +T −D").
        const setDiff = this.scopeDiff(g.game_scope);
        if (setDiff) {
            const setEl = document.createElement('span');
            setEl.className = 'admin-grid-scope';
            setEl.textContent = setDiff;
            setEl.title = `Game set: ${g.game_scope}`;
            metaParts.push(setEl);
        }

        if (sched) {
            metaParts.push(`${sched.challenge.title} (${shortDate(sched.challenge.starts_at)})`);
            if (frozen) {
                const pub = document.createElement('span');
                pub.className = 'admin-draft admin-draft-frozen';
                pub.textContent = `published ${shortDate(sched.challenge.synced_at!)} → #${sched.challenge.remote_id}`;
                metaParts.push(pub);
            }
        }
        for (const [i, part] of metaParts.entries()) meta.append(i === 0 ? ' ' : ' · ', part);
        info.appendChild(identity);

        // Line 2 — the scores, on their own line so a row's par reads as a unit
        // instead of trailing off the end of its identity.
        const scores = document.createElement('div');
        scores.className = 'admin-grid-scores';

        // Always show both reference players so resolution state is glanceable:
        // the draft's score when it exists (missing games are baked into it), an
        // em-dash when absent. Vortex grids have no solver, so Quadrant is omitted.
        // Once scheduled, the attached par is what counts, so show that instead —
        // and flag a par improved since the last push, which the remote won't serve
        // until the next sync.
        const refs = g.is_vortex ? ['Gyrodiot'] : ['Quadrant', 'Gyrodiot'];
        refs.forEach((name, i) => {
            if (i > 0) scores.append(' · ');
            const d = g.drafts.find(x => x.player === name);
            const par = sched?.refs.find(x => x.player === name);
            const shown = sched ? (par?.total ?? null) : (d?.score ?? null);
            const span = document.createElement('span');
            span.className = shown !== null ? 'admin-draft admin-draft-done' : 'admin-draft admin-draft-pending';
            const penalty = sched && par?.unsolved ? ` (${par.unsolved} unsolved)` : '';
            span.textContent = shown !== null ? `${name} ${shown}${penalty}` : `${name} —`;
            scores.append(span);
            if (par?.unpushed) {
                const drift = document.createElement('span');
                drift.className = 'admin-draft admin-draft-drift';
                drift.textContent = ' ⚠ unpushed';
                drift.title = 'This par improved after the last sync — run Sync to publish it to the remote';
                scores.append(drift);
            }
        });
        // Capture-ready hint: how much you've solved on your local copy. Shown only
        // when you've actually won something locally (i.e. there's a draft to capture).
        const localSolved = this.callbacks.onLocalPlayState(g.grid_data);
        if (localSolved) {
            const hint = document.createElement('span');
            hint.className = 'admin-draft admin-local-hint';
            hint.textContent = ` · local ${localSolved}/12`;
            scores.append(hint);
        }
        info.appendChild(scores);
        row.appendChild(info);

        const actions = document.createElement('div');
        actions.className = 'admin-grid-actions';
        actions.appendChild(this.button('Play', () => this.callbacks.onPlay(g.grid_data)));
        if (!g.is_vortex) {
            actions.appendChild(this.button('Solve', () => this.doSolve(g.id)));
        }
        const capture = this.button('Capture', () => this.doCapture(g));
        capture.title = 'Store your local play of this grid as the Gyrodiot reference draft';
        actions.appendChild(capture);
        if (opts.allowSchedule !== false) {
            actions.appendChild(this.button('Schedule', () => this.doSchedule(g.id, g.game_scope ?? this.allScope)));
        }
        // Once a challenge is on the remote it has a player leaderboard to fetch — a
        // per-row shortcut to pull just this one (the header button pulls them all).
        if (sched && sched.challenge.remote_id != null) {
            const open = this.boards.has(sched.challenge.id);
            const lb = this.button(open ? 'Hide board' : 'Leaderboard', () => this.toggleBoard(sched.challenge.id));
            lb.classList.add('admin-btn-secondary');
            if (open) lb.classList.add('admin-btn-active');
            lb.title = 'Show/hide this challenge’s player leaderboard from the remote';
            actions.appendChild(lb);
        }
        // A scheduled challenge stays movable right up until it is pushed; after that
        // the remote is serving it and the local copy is a read-only mirror.
        if (sched && !frozen) {
            actions.appendChild(this.slotPicker(sched));
            actions.appendChild(this.button('Reschedule', () => this.doReschedule(sched)));
            const un = this.button('Unschedule', () => this.doUnschedule(sched));
            un.classList.add('admin-btn-danger');
            actions.appendChild(un);
        }
        if (opts.allowDelete) {
            const del = this.button('Delete', () => this.doDelete(g));
            del.classList.add('admin-btn-danger');
            actions.appendChild(del);
        }
        row.appendChild(actions);

        return row;
    }

    /** The free slots a challenge may move to: its own track's, never the other's. */
    private slotsFor(g: ScheduledGrid): Slot[] {
        return g.is_vortex ? this.slots.vortex : this.slots.base;
    }

    /** Free-slot dropdown for a reschedule, defaulting to the challenge's own slot. */
    private slotPicker(g: ScheduledGrid): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'admin-input admin-slot-picker';
        const current = g.challenge.starts_at;
        // The row's own slot is not in `slots` (it is taken — by this challenge), so
        // seed it as the selected default: the picker opens as a no-op, not a move.
        const options: Slot[] = [{ starts_at: current, ends_at: g.challenge.ends_at }, ...this.slotsFor(g)];
        for (const s of options) {
            const o = document.createElement('option');
            o.value = s.starts_at;
            o.textContent = shortDate(s.starts_at) + (s.starts_at === current ? ' (current)' : '');
            sel.appendChild(o);
        }
        sel.value = this.slotChoice.get(g.challenge.id) ?? current;
        sel.addEventListener('change', () => this.slotChoice.set(g.challenge.id, sel.value));
        return sel;
    }

    private button(text: string, onClick: () => void): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = 'admin-btn';
        b.textContent = text;
        b.addEventListener('click', onClick);
        return b;
    }

    private label(text: string): HTMLElement {
        const s = document.createElement('span');
        s.className = 'admin-label';
        s.textContent = text;
        return s;
    }
}
